/**
 * Legacy-compatibility shim that resolves the old per-locale `translations`
 * map into a `MultiLanguageString`, plus the accessors that read one.
 *
 * `useLegacyTranslatedField` is temporary: it exists only for content still
 * stored in the old format (a source-language value beside a
 * `translations[locale][field]` map) that doesn't yet pass through an oRPC
 * wire mapper — the `question` collection and the teaser `CommentPreview`. It
 * runs, on the client, the same `resolveMultiLanguageString` step the wire
 * mappers run server-side, so callers read the result with the same
 * `displayTextOf` / `originalTextOf` / `useDisplayText` accessors used on a
 * value that arrived already resolved. Each call site goes away as its source
 * moves to a server-resolved read.
 */

import { displayTextOf, hasTranslationFor, type MultiLanguageString, resolveMultiLanguageString } from '@repo/shared/orpc/multi-language';
import type { WithTranslations } from '@repo/shared/types';
import { useLanguage } from 'component/translation';
import { useSessionData, useSiloKey } from 'util/datastore';
import { isMultiLanguageSiloKey } from 'util/siloLanguages';
import { getDeploymentConfig } from 'util/util';

// The string-valued keys of T — the fields that can carry translatable text.
type StringFieldKey<T> = { [K in keyof T]-?: NonNullable<T[K]> extends string ? K : never }[keyof T] & string;

/**
 * Resolves a translatable object field still stored in the legacy
 * `translations` format (e.g. a `Question`'s `text`) into a
 * `MultiLanguageString`. Temporary — drop once the field is resolved by a
 * server-side wire mapper instead.
 */
export function useLegacyTranslatedField<T extends WithTranslations>(object: T | null, field: StringFieldKey<T>): MultiLanguageString {
    const isoLocale = useLanguage();
    // `field` is constrained to string-valued keys, but TS can't narrow the
    // indexed access over a computed key union, so assert the string result.
    const text = (object?.[field] ?? '') as string;
    return resolveMultiLanguageString({ text, translations: object?.translations, field, locale: isoLocale });
}

/**
 * The text to display for a `MultiLanguageString`, respecting the user's
 * "show original" toggle stored in session data at `['showOriginal', key]`.
 */
export function useDisplayText(value: MultiLanguageString | undefined, key: string): string {
    const isoLocale = useLanguage();
    const showOriginal = useSessionData(['showOriginal', key]);
    if (value === undefined) return '';
    if (typeof value === 'string') return value;
    return showOriginal ? value.originalText : displayTextOf(value, isoLocale);
}

/**
 * Whether a `MultiLanguageString` carries a translation the current viewer
 * would actually see. A value can carry other locales — an every-locale read
 * sends them all — so carrying translations at all is not the same question.
 */
export function useHasTranslation(value: MultiLanguageString | undefined): boolean {
    const isoLocale = useLanguage();
    return value !== undefined && hasTranslationFor(value, isoLocale);
}

export function useIsMultiLanguageSilo(): boolean {
    return isMultiLanguageSiloKey(useSiloKey());
}

/**
 * Country code (e.g. `'CH'`) the current silo wants rendered as a prefix on
 * language flags, or `null` for no prefix. Driven by
 * `languageFlagCountryPrefix` in `psi.config.ts` so partner-specific behavior
 * stays out of core components.
 */
export function useLanguageFlagCountryPrefix(): 'CH' | null {
    const siloKey = useSiloKey();
    return getDeploymentConfig()?.siloConfig?.[siloKey]?.languageFlagCountryPrefix ?? null;
}
